import { Component, Input, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { TimeFrameMode } from 'src/app/shared/interfaces/global-filter-model';
import { GlobalFilterService } from 'src/app/shared/services/global-filter.service';
import { MondelezDataService } from 'src/app/shared/services/mondelez-data.service';

@Component({
  selector: 'app-inventory-chart',
  templateUrl: './inventory-chart.component.html',
  styleUrls: ['./inventory-chart.component.scss'],
})
export class InventoryChartComponent {
  @Input() inventoryData: any;
  @Input() showTimeFrameModeToggle = true;
  public tempValues: any;
  public chartObj;
  private yearRange = {
    startYear: undefined,
    endYear: undefined,
  };
  public timeFrameMode: TimeFrameMode;
  public aggregatedData = [];
  public chartParams = [];
  public chartData = {
    year: {},
    quarter: {},
  };

  private subs = new Subscription();

  constructor(
    private mondelezDataService: MondelezDataService,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.timeFrameMode) {
      this.prepareChart();
      this.aggregatedData = this.aggregator();
    }
  }

  ngOnInit(): void {
    this.subs.add(
      this.globalFilterService.globalFilters.subscribe((filter) => {
        if (filter) {
          this.yearRange.startYear = filter.start_year;
          this.yearRange.endYear = filter.end_year;
        }
      }),
    );
    this.subs.add(
      this.mondelezDataService.timeFrameMode.subscribe((value) => {
        this.timeFrameMode = value;
        this.prepareChart();
        this.aggregatedData = this.aggregator();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private prepareChart() {
    this.chartParams = [];
    if (this.inventoryData[this.timeFrameMode]['demand']) {
      this.chartParams.push({
        name: 'dos',
        type: 'line',
        label: 'Days of Supply',
        color: '#6C4688',
        border: '#6C4688',
        bg: '#EAEAF2',
        stacked: true,
        unit: '',
        legend: { isTop: true, isRight: false },
      });
      this.chartData[this.timeFrameMode]['dos'] = this.calculateDaysOfSupply();
    }
    if (this.inventoryData[this.timeFrameMode]['DC']) {
      this.chartParams.push({
        name: 'dc',
        type: 'bar',
        label: 'Distribution Center Inventory',
        color: '#747480',
        stacked: true,
        titleY: 'Inventory (MMlbs)',
      });
      this.chartData[this.timeFrameMode]['dc'] = this.inventoryData[this.timeFrameMode]['DC'];
    }
    if (this.inventoryData[this.timeFrameMode]['Branch']) {
      this.chartParams.push({
        name: 'branch',
        type: 'bar',
        label: 'Branches Inventory',
        color: '#C4C4CD',
        stacked: true,
      });
      this.chartData[this.timeFrameMode]['branch'] = this.inventoryData[this.timeFrameMode]['Branch'];
    }
    if (this.inventoryData[this.timeFrameMode]['transit']) {
      this.chartParams.push({
        name: 'transit',
        type: 'bar',
        label: 'Inventory in Transit',
        color: '#E1E1E6',
        stacked: true,
      });
      this.chartData[this.timeFrameMode]['transit'] = this.inventoryData[this.timeFrameMode]['transit'];
    }
  }

  private aggregator() {
    if (this.inventoryData != undefined) {
      const countryList = ['Distribution Center', 'Branches', 'In Transit', 'Total'];
      const aggData = countryList.map((el) => {
        return { label: el, value: 33, prefix: '', suffix: 'MM lbs' };
      });
      return [
        {
          label: 'Distribution Center',
          value: this.inventoryData.year['DC'] ? Math.round(this.inventoryData.year['DC'].reduce((acc, cur) => acc + cur, 0) / 1000000) : 0,
          prefix: '',
          suffix: 'MM lbs',
        },
        {
          label: 'Branches',
          value: this.inventoryData.year['Branch'] ? Math.round(this.inventoryData.year['Branch'].reduce((acc, cur) => acc + cur, 0) / 1000000) : 0,
          prefix: '',
          suffix: 'MM lbs',
        },
        {
          label: 'In Transit',
          value: this.inventoryData.year['transit'] ? Math.round(this.inventoryData.year['transit'].reduce((acc, cur) => acc + cur, 0) / 1000000) : 0,
          prefix: '',
          suffix: 'MM lbs',
        },
        {
          label: 'Total',
          value: this.inventoryData.year['total'] ? Math.round(this.inventoryData.year['total'].reduce((acc, cur) => acc + cur, 0) / 1000000) : 0,
          prefix: '',
          suffix: 'MM lbs',
        },
      ];
    } else {
      return [];
    }
  }

  onTimeFrameChange(value: TimeFrameMode) {
    this.mondelezDataService.updateTimeFrameMode(value);
  }

  calculateDaysOfSupply() {
    if (this.inventoryData != undefined) {
      let demandData = this.inventoryData[this.timeFrameMode]?.demand || [];
      let totalData = this.inventoryData[this.timeFrameMode]?.total || [];
      let transitData = this.inventoryData[this.timeFrameMode]?.transit || [];
      let supplyDays = [];
      demandData.forEach((demand, index) => {
        const value = (totalData[index] * 90) / demand;
        supplyDays.push(Math.round(value));
      });
      return supplyDays;
    } else {
      return [];
    }
  }
}
